/** Dibujo a mano alzada: trazos por usuario, en coordenadas del canvas. Puro. */
import type { Trazo } from './types';

export const COLORES_TRAZO = ['#1644EB', '#EF4444', '#10B981', '#F59E0B', '#02062B']; // hex-de-marca-ok: paleta de trazos que elige el usuario
export const ANCHOS_TRAZO = [2, 4, 8];

export function nuevoTrazo(autor: { id: string; nombre: string }, color: string, ancho: number, x: number, y: number): Trazo {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 10) : Math.random().toString(36).slice(2, 12);
  return { id, autorId: autor.id, autorNombre: autor.nombre, color, ancho, puntos: [x, y], creadoEn: new Date().toISOString() };
}

/** Agrega un punto si se movió al menos `min` px desde el último (evita miles de puntos). */
export function agregarPunto(t: Trazo, x: number, y: number, min = 1.5): Trazo {
  const n = t.puntos.length;
  if (n >= 2) {
    const dx = x - t.puntos[n - 2];
    const dy = y - t.puntos[n - 1];
    if (dx * dx + dy * dy < min * min) return t;
  }
  return { ...t, puntos: [...t.puntos, x, y] };
}

/** `d` del <path>: un punto solo dibuja un puntito. */
export function pathDe(puntos: number[]): string {
  if (puntos.length < 2) return '';
  if (puntos.length === 2) return `M ${puntos[0]} ${puntos[1]} l 0.01 0`;
  let d = `M ${puntos[0]} ${puntos[1]}`;
  for (let i = 2; i < puntos.length; i += 2) d += ` L ${puntos[i]} ${puntos[i + 1]}`;
  return d;
}

export function autoresDe(trazos: Trazo[]): Array<{ id: string; nombre: string; cantidad: number }> {
  const m = new Map<string, { id: string; nombre: string; cantidad: number }>();
  for (const t of trazos) {
    const a = m.get(t.autorId);
    if (a) a.cantidad += 1;
    else m.set(t.autorId, { id: t.autorId, nombre: t.autorNombre, cantidad: 1 });
  }
  return Array.from(m.values());
}

/** Caja del trazo, para que la capa SVG tenga tamaño aunque esté en coordenadas negativas. */
export function cajaDe(trazos: Trazo[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of trazos)
    for (let i = 0; i < t.puntos.length; i += 2) {
      minX = Math.min(minX, t.puntos[i]);
      maxX = Math.max(maxX, t.puntos[i]);
      minY = Math.min(minY, t.puntos[i + 1]);
      maxY = Math.max(maxY, t.puntos[i + 1]);
    }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

/** Distancia de un punto al segmento AB. */
export function distanciaASegmento(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** `true` si el punto pasa a menos de `radio` (+ medio grosor) de algún segmento del trazo. */
export function tocaTrazo(t: Trazo, x: number, y: number, radio: number): boolean {
  const tol = radio + t.ancho / 2;
  const p = t.puntos;
  if (p.length < 2) return false;
  if (p.length === 2) return Math.hypot(x - p[0], y - p[1]) <= tol;
  for (let i = 0; i + 3 < p.length; i += 2) {
    if (distanciaASegmento(x, y, p[i], p[i + 1], p[i + 2], p[i + 3]) <= tol) return true;
  }
  return false;
}

/** Ids de los trazos que toca la goma en (x, y). Para el barrido: se llama en cada movimiento. */
export function trazosTocados(trazos: Trazo[], x: number, y: number, radio: number): string[] {
  return trazos.filter((t) => tocaTrazo(t, x, y, radio)).map((t) => t.id);
}
